import React, { useState, useCallback, useContext, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { theme } from '../../../styles/theme';
import { BottomNav } from '../../common/BottomNav';
import { FeedbackLink } from '../../common/FeedbackLink';
import { AuthContext } from '../../../context/AuthContext';
import { DataContext } from '../../../context/DataContext';
import { processImage, validateFileSize, validateFileType } from '../../../utils/imageUtils';
import { validateDocumentType, validateDocumentSize, formatFileSize } from '../../../utils/fileUtils';
import { parseVideoUrl, getVideoThumbnail, fetchVimeoThumbnail } from '../../../utils/videoUtils';
import { formatLevelDisplay } from '../../../utils/levelUtils';
import { getUserMilestones, awardMilestone, getUserActivityCount, getUserCauses, getUserTemplates, uploadDocument, updateActivity } from '../../../firebase/services';
import SuccessModal from '../../common/SuccessModal';
import ShareModal from '../../common/ShareModal';
import MilestoneModal from '../../common/MilestoneModal';

const Container = styled.div`
  background-color: ${theme.colors.background};
  padding: ${theme.spacing.md};
  max-width: 400px;
  margin: 0 auto;
  font-family: ${theme.typography.bodyFont};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
`;

const BackButton = styled.div`
  margin-right: ${theme.spacing.md};
  cursor: pointer;
  font-size: 24px;
`;

const HeaderTitle = styled.h1`
  font-family: ${theme.typography.headingFont};
  font-size: ${theme.typography.fontSizes.xlarge};
  font-weight: bold;
  color: ${theme.colors.deepNavy};
  margin: 0;
`;

const SectionTitle = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.fontSizes.medium};
  color: ${theme.colors.slateGray};
  margin-bottom: ${theme.spacing.sm};
  margin-top: ${theme.spacing.lg};
`;

const CauseSelector = styled.div`
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.medium};
  padding: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.sm};
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid ${({ selected }) => (selected ? theme.colors.brandYellow : theme.colors.softGray)};
  cursor: pointer;
`;

const CauseTextContainer = styled.div``;

const CauseTitle = styled.h2`
  font-family: ${theme.typography.headingFont};
  font-size: ${theme.typography.fontSizes.large};
  color: ${theme.colors.deepNavy};
  font-weight: bold;
  margin: 0;
`;

const CauseSubtitle = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.fontSizes.regular};
  color: ${theme.colors.slateGray};
  margin: 0;
`;

const RadioButton = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 12px;
  border: 2px solid ${({ selected }) => (selected ? theme.colors.brandYellow : theme.colors.softGray)};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RadioButtonInner = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 6px;
  background-color: ${theme.colors.brandYellow};
`;

const StyledInput = styled.input`
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.medium};
  padding: ${theme.spacing.md};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.fontSizes.medium};
  border: 1px solid ${theme.colors.softGray};
  margin-bottom: ${theme.spacing.lg};
  width: calc(100% - 34px);
`;

const StyledTextArea = styled.textarea`
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.medium};
  padding: ${theme.spacing.md};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.fontSizes.medium};
  border: 1px solid ${theme.colors.softGray};
  margin-bottom: ${theme.spacing.lg};
  width: calc(100% - 34px);
  height: 100px;
  resize: vertical;
`;

const DropzoneContainer = styled.div`
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.medium};
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed ${theme.colors.softGray};
  margin-bottom: ${theme.spacing.lg};
  cursor: pointer;
  outline: none;
`;

const PlusIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 20px;
  background-color: ${theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${theme.colors.softGray};
  color: ${theme.colors.softGray};
  font-size: 24px;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: ${theme.borderRadius.medium};
`;

const LogButton = styled.button`
  background-color: ${theme.colors.brandYellow};
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.medium};
  text-align: center;
  border: none;
  cursor: pointer;
  width: 100%;
  &:disabled {
    background-color: ${theme.colors.softGray};
    cursor: not-allowed;
  }
`;

const LogButtonText = styled.span`
  font-family: ${theme.typography.headingFont};
  font-size: ${theme.typography.fontSizes.large};
  color: ${theme.colors.deepNavy};
  font-weight: bold;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 8px;
  background-color: ${theme.colors.softGray};
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: ${theme.spacing.md};
`;

const ProgressBar = styled.div`
  height: 100%;
  background-color: ${theme.colors.brandYellow};
  transition: width 0.3s ease-in-out;
  width: ${props => props.progress}%;
`;

const ProgressText = styled.p`
  font-size: ${theme.typography.fontSizes.small};
  color: ${theme.colors.slateGray};
  text-align: center;
  margin: ${theme.spacing.sm} 0;
`;


const LogActivityScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser} = useContext(AuthContext);
  const { causes, addActivity } = useContext(DataContext);
  const [selectedCause, setSelectedCause] = useState(null);
  const [activity, setActivity] = useState('');
  const [reflection, setReflection] = useState('');
  const [image, setImage] = useState(null);
  const [processedImages, setProcessedImages] = useState(null);
  const [documentFile, setDocumentFile] = useState(null);
  const [documentError, setDocumentError] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [videoError, setVideoError] = useState('');
  const [videoPreview, setVideoPreview] = useState(null);
  const [documentLinks, setDocumentLinks] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [savedActivity, setSavedActivity] = useState(null);
  const [pendingMilestones, setPendingMilestones] = useState([]);
  const [currentMilestone, setCurrentMilestone] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const hasSetDefaultCause = useRef(false);

  // Handle pre-filled data from Get Inspired
  useEffect(() => {
    if (location.state?.prefilledActivity) {
      setActivity(location.state.prefilledActivity);
    }
    if (location.state?.prefilledCause) {
      setSelectedCause(location.state.prefilledCause);
      hasSetDefaultCause.current = true;
    }
  }, [location.state]);

  useEffect(() => {
    if (causes && causes.length > 0 && !location.state?.prefilledCause && !hasSetDefaultCause.current) {
      setSelectedCause(causes[0].id);
      hasSetDefaultCause.current = true;
    }
  }, [causes, location.state?.prefilledCause]);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const result = await getUserTemplates(currentUser.uid);
        setTemplates(result);
      } catch (error) {
        console.error('Failed to fetch templates:', error);
      }
    };
    fetchTemplates();
  }, [currentUser.uid]);

  useEffect(() => {
    if (!showSuccessModal) {
      const fetchTemplates = async () => {
        try {
          const result = await getUserTemplates(currentUser.uid);
          setTemplates(result);
        } catch (error) {
          console.error('Failed to refresh templates:', error);
        }
      };
      fetchTemplates();
    }
  }, [showSuccessModal, currentUser.uid]);

  // Check and award milestones after logging an activity
  // Returns true if any NEW milestones should be shown
  const checkAndAwardMilestones = async (hasPhoto, hasReflection) => {
    try {
      const [earned, totalCount, userCauses] = await Promise.all([
        getUserMilestones(currentUser.uid),
        getUserActivityCount(currentUser.uid),
        getUserCauses(currentUser.uid)
      ]);

      // Check if this is a backfill situation (existing user with no recorded milestones)
      // If user has more than 1 activity but no milestones, they're an existing user
      const isBackfillNeeded = earned.length === 0 && totalCount > 1;

      // Milestones to award (save to DB)
      const toAward = [];
      // Milestones to show (only new ones triggered by THIS activity)
      const toShow = [];

      // First activity - only show if this IS their first (totalCount === 1)
      if (!earned.includes('first_activity') && totalCount >= 1) {
        toAward.push('first_activity');
        if (totalCount === 1) toShow.push('first_activity');
      }

      // Photo - show if this activity has a photo and they haven't earned it
      if (!earned.includes('first_photo') && hasPhoto) {
        toAward.push('first_photo');
        if (!isBackfillNeeded) toShow.push('first_photo');
      }

      // Reflection - show if this activity has a reflection and they haven't earned it
      if (!earned.includes('first_reflection') && hasReflection) {
        toAward.push('first_reflection');
        if (!isBackfillNeeded) toShow.push('first_reflection');
      }

      // Count milestones - only show if THIS activity crosses the threshold
      if (!earned.includes('five_activities') && totalCount >= 5) {
        toAward.push('five_activities');
        if (totalCount === 5) toShow.push('five_activities');
      }
      if (!earned.includes('ten_activities') && totalCount >= 10) {
        toAward.push('ten_activities');
        if (totalCount === 10) toShow.push('ten_activities');
      }
      if (!earned.includes('twenty_five_activities') && totalCount >= 25) {
        toAward.push('twenty_five_activities');
        if (totalCount === 25) toShow.push('twenty_five_activities');
      }

      // Multi-cause - only show if not backfilling
      if (!earned.includes('multi_cause') && userCauses.length >= 2) {
        toAward.push('multi_cause');
        if (!isBackfillNeeded) toShow.push('multi_cause');
      }

      // Award all milestones to DB (silently for backfill)
      for (const key of toAward) {
        await awardMilestone(currentUser.uid, key);
      }

      // Only show modals for new milestones
      if (toShow.length > 0) {
        setPendingMilestones(toShow.slice(1));
        setCurrentMilestone(toShow[0]);
        return true;
      }
      return false;
    } catch (e) {
      console.error('Milestone check failed:', e);
      // Fail silently — don't block user flow
      return false;
    }
  };

  // Handle milestone dismissal
  const handleMilestoneDismiss = () => {
    if (pendingMilestones.length > 0) {
      setCurrentMilestone(pendingMilestones[0]);
      setPendingMilestones(prev => prev.slice(1));
    } else {
      setCurrentMilestone(null);
      // Show success modal after all milestones shown
      setShowSuccessModal(true);
    }
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) {
      return;
    }

    const file = acceptedFiles[0];

    // Validate file
    if (!validateFileType(file)) {
      alert('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    if (!validateFileSize(file)) {
      alert('File size must be less than 5MB');
      return;
    }

    // Show preview immediately
    setImage(Object.assign(file, {
      preview: URL.createObjectURL(file)
    }));

    // Process image in background
    try {
      setProgressMessage('Optimizing image...');
      const processed = await processImage(file, (progress) => {
        setUploadProgress(progress);
      });
      setProcessedImages(processed);
      setProgressMessage('Image ready!');
      setTimeout(() => {
        setProgressMessage('');
        setUploadProgress(0);
      }, 1500);
    } catch (error) {
      console.error('Error processing image:', error);
      alert('Error optimizing image. Please try again.');
      setImage(null);
      setProcessedImages(null);
    }
  }, []);

  const handleDocumentSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setDocumentError('');

    if (!validateDocumentType(file)) {
      setDocumentError(t('logActivity.docTypeError'));
      return;
    }

    if (!validateDocumentSize(file)) {
      setDocumentError(t('logActivity.docSizeError'));
      return;
    }

    setDocumentFile(file);
  };

  const handleDocumentRemove = () => {
    setDocumentFile(null);
    setDocumentError('');
  };

  const handleVideoUrlBlur = async () => {
    if (!videoUrl.trim()) {
      setVideoPreview(null);
      setVideoError('');
      return;
    }

    const parsed = parseVideoUrl(videoUrl);
    if (!parsed) {
      setVideoPreview(null);
      setVideoError(t('logActivity.videoError'));
      return;
    }

    setVideoError('');

    if (parsed.platform === 'youtube') {
      setVideoPreview({
        platform: 'youtube',
        videoId: parsed.videoId,
        thumbnailUrl: getVideoThumbnail('youtube', parsed.videoId)
      });
    } else if (parsed.platform === 'vimeo') {
      const thumb = await fetchVimeoThumbnail(parsed.url);
      setVideoPreview({
        platform: 'vimeo',
        videoId: parsed.videoId,
        thumbnailUrl: thumb
      });
    }
  };

  const handleLogActivity = async () => {
    if (isSubmitting) {
      return;
    }

    if (!activity || !activity.trim()) {
      alert('Please enter an activity description.');
      return;
    }

    if (!currentUser) {
      console.error('LogActivityScreen Error: User not logged in.');
      alert('You must be logged in to log an activity.');
      return;
    }

    const causeData = causes.find(cause => cause.id === selectedCause);
    if (!causeData) {
      console.error('LogActivityScreen Error: Selected cause not found.');
      alert('Please select a cause.');
      return;
    }

    const activityData = {
      userId: currentUser.uid,
      causeId: causeData.id,
      title: activity,
      description: reflection,
      reflection: reflection,
      cause: causeData,
      activity,
      timestamp: new Date(),
      ...(videoUrl.trim() && { videoUrl: videoUrl.trim() }),
      ...(documentLinks.trim() && { documentLinks: documentLinks.trim() }),
    };

    try {
      setIsSubmitting(true);
      setProgressMessage('Uploading activity...');

      // Pass the processed images to addActivity
      const savedActivityData = await addActivity(activityData, processedImages, (progress) => {
        setUploadProgress(progress);
      });

      // Upload document if selected
      let documentUrl = '';
      let documentName = '';
      if (documentFile) {
        setProgressMessage('Uploading document...');
        const docResult = await uploadDocument(currentUser.uid, savedActivityData.id, documentFile);
        documentUrl = docResult.url;
        documentName = docResult.name;
        await updateActivity(savedActivityData.id, { documentUrl, documentName });
      }

      setProgressMessage('Activity logged successfully!');
      setDocumentFile(null);
      setDocumentError('');
      setVideoUrl('');
      setVideoError('');
      setVideoPreview(null);
      setDocumentLinks('');

      // Store the saved activity for sharing
      setSavedActivity({
        ...activityData,
        id: savedActivityData.id,
        imageUrl: savedActivityData.imageUrl,
        thumbnailUrl: savedActivityData.thumbnailUrl,
        ...(documentUrl && { documentUrl, documentName }),
      });

      // Check for milestones (will show milestone modals before success modal)
      const hasPhoto = !!processedImages;
      const hasReflection = !!reflection.trim();
      const milestonesAwarded = await checkAndAwardMilestones(hasPhoto, hasReflection);

      // If no milestones were awarded, show success modal directly
      // (If milestones were awarded, handleMilestoneDismiss will show it after)
      if (!milestonesAwarded) {
        setShowSuccessModal(true);
      }

    } catch (error) {
      console.error('LogActivityScreen Error: Failed to log activity.', error);
      alert('Failed to log activity. Please try again.');
      setProgressMessage('');
      setUploadProgress(0);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleShareNow = () => {
    setShowSuccessModal(false);
    setShowShareModal(true);
  };

  const handleMaybeLater = () => {
    setShowSuccessModal(false);
    navigate('/progress');
  };

  const handleCloseShareModal = () => {
    setShowShareModal(false);
    navigate('/progress');
  };

  const handleApplyTemplate = (template) => {
    const matchedCause = causes.find(c => c.title === template.causeTitle);
    if (matchedCause) {
      setSelectedCause(matchedCause.id);
    }
    setActivity(template.title);
    setShowTemplateModal(false);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/gif': ['.gif'],
      'image/*': [] // Allow all images on mobile
    },
    multiple: false,
    onDropRejected: (fileRejections) => {
      console.error('File rejected:', fileRejections);
      alert('Please select a valid image file (JPG, PNG, or GIF)');
    }
  });

  return (
    <Container>
      <Header>
        <BackButton onClick={() => window.history.back()}>
          {'←'}
        </BackButton>
        <HeaderTitle>{t('logActivity.title')}</HeaderTitle>
      </Header>

      {templates.length > 0 && (
        <div style={{ textAlign: 'right', marginBottom: '8px' }}>
          <span
            style={{ color: '#007bff', fontSize: '13px', fontFamily: theme.typography.bodyFont, cursor: 'pointer' }}
            onClick={() => setShowTemplateModal(true)}
          >
            {t('logActivity.myTemplates')} →
          </span>
        </div>
      )}

      <SectionTitle>{t('logActivity.whichCause')}</SectionTitle>
      {causes.map(cause => (
        <CauseSelector key={cause.id} selected={selectedCause === cause.id} onClick={() => setSelectedCause(cause.id)}>
          <CauseTextContainer>
            <CauseTitle>{cause.title}</CauseTitle>
            <CauseSubtitle>{formatLevelDisplay(cause.level)}</CauseSubtitle>
          </CauseTextContainer>
          <RadioButton selected={selectedCause === cause.id}>
            {selectedCause === cause.id && <RadioButtonInner />}
          </RadioButton>
        </CauseSelector>
      ))}

      <SectionTitle>{t('logActivity.whatDidYouDo')}</SectionTitle>
      <StyledInput
        type="text"
        value={activity}
        onChange={(e) => setActivity(e.target.value)}
      />

      <SectionTitle>{t('logActivity.photo')}</SectionTitle>
      <DropzoneContainer {...getRootProps()}>
        <input {...getInputProps()} />
        {image ? (
          <PreviewImage src={image.preview} alt="Preview" />
        ) : (
          <PlusIcon>+</PlusIcon>
        )}
      </DropzoneContainer>

      {/* Document Attachment */}
      <div style={{ marginTop: '16px' }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '600',
          color: '#333',
          marginBottom: '8px'
        }}>
          {t('logActivity.attachDocument')} {t('common.optional')}
        </label>
        <div style={{ fontSize: '12px', color: '#999', marginBottom: '8px' }}>
          {t('logActivity.documentPrivateHelper')}
        </div>

        {!documentFile ? (
          <div style={{
            border: '2px dashed #ccc',
            borderRadius: '8px',
            padding: '16px',
            textAlign: 'center',
            cursor: 'pointer',
            position: 'relative'
          }}>
            <input
              type="file"
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={handleDocumentSelect}
              style={{
                position: 'absolute',
                top: 0, left: 0, width: '100%', height: '100%',
                opacity: 0, cursor: 'pointer'
              }}
            />
            <div style={{ fontSize: '24px', marginBottom: '4px' }}>📎</div>
            <div style={{ fontSize: '14px', color: '#666' }}>
              {t('logActivity.tapToAttachDoc')}
            </div>
            <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
              {t('logActivity.maxFileSize')}
            </div>
          </div>
        ) : (
          <div style={{
            border: '1px solid #ddd',
            borderRadius: '8px',
            padding: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
              <span style={{ fontSize: '20px', flexShrink: 0 }}>📄</span>
              <div style={{ minWidth: 0 }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {documentFile.name}
                </div>
                <div style={{ fontSize: '12px', color: '#999' }}>
                  {formatFileSize(documentFile.size)}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={handleDocumentRemove}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '18px',
                color: '#999',
                cursor: 'pointer',
                padding: '4px 8px',
                flexShrink: 0
              }}
            >
              ✕
            </button>
          </div>
        )}

        {documentError && (
          <div style={{ color: '#e74c3c', fontSize: '13px', marginTop: '6px' }}>
            {documentError}
          </div>
        )}
      </div>

      {/* Public Document Links */}
      <div style={{ marginTop: '16px' }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '600',
          color: '#333',
          marginBottom: '4px'
        }}>
          {t('logActivity.documentLinks')} {t('common.optional')}
        </label>
        <div style={{ fontSize: '12px', color: '#999', marginBottom: '8px' }}>
          {t('logActivity.documentLinksHelper')}
        </div>
        <textarea
          value={documentLinks}
          onChange={(e) => setDocumentLinks(e.target.value)}
          placeholder={"https://example.gov/report.pdf\nhttps://research.org/study"}
          rows={3}
          style={{
            width: '100%',
            padding: '10px 12px',
            fontSize: '14px',
            border: '1px solid #ddd',
            borderRadius: '8px',
            boxSizing: 'border-box',
            outline: 'none',
            resize: 'vertical',
            fontFamily: 'inherit',
            lineHeight: '1.5'
          }}
        />
      </div>

      {/* Video Link */}
      <div style={{ marginTop: '16px' }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '600',
          color: '#333',
          marginBottom: '8px'
        }}>
          {t('logActivity.videoLink')}
        </label>
        <input
          type="url"
          value={videoUrl}
          onChange={(e) => {
            setVideoUrl(e.target.value);
            setVideoError('');
          }}
          onBlur={handleVideoUrlBlur}
          placeholder={t('logActivity.videoLinkPlaceholder')}
          style={{
            width: '100%',
            padding: '10px 12px',
            fontSize: '14px',
            border: '1px solid #ddd',
            borderRadius: '8px',
            boxSizing: 'border-box',
            outline: 'none'
          }}
        />
        {videoError && (
          <div style={{ color: '#e74c3c', fontSize: '13px', marginTop: '6px' }}>
            {videoError}
          </div>
        )}
        {videoPreview && videoPreview.thumbnailUrl && (
          <div style={{
            marginTop: '8px',
            position: 'relative',
            display: 'inline-block',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <img
              src={videoPreview.thumbnailUrl}
              alt="Video thumbnail"
              style={{
                width: '160px',
                height: '90px',
                objectFit: 'cover',
                display: 'block'
              }}
            />
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              backgroundColor: 'rgba(0,0,0,0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <div style={{
                width: 0,
                height: 0,
                borderLeft: '12px solid white',
                borderTop: '7px solid transparent',
                borderBottom: '7px solid transparent',
                marginLeft: '2px'
              }} />
            </div>
          </div>
        )}
        {videoPreview && !videoPreview.thumbnailUrl && (
          <div style={{
            marginTop: '8px',
            fontSize: '13px',
            color: '#666'
          }}>
            {t('logActivity.videoThumbnailUnavailable')}
          </div>
        )}
      </div>

      <SectionTitle>{t('logActivity.reflection')}</SectionTitle>
      <StyledTextArea
        value={reflection}
        onChange={(e) => setReflection(e.target.value)}
        placeholder={t('logActivity.reflectionPlaceholder')}
      />

      {/* Upload Progress */}
      {(uploadProgress > 0 || progressMessage) && (
        <>
          <ProgressBarContainer>
            <ProgressBar progress={uploadProgress} />
          </ProgressBarContainer>
          {progressMessage && <ProgressText>{progressMessage}</ProgressText>}
        </>
      )}

      <LogButton
        onClick={handleLogActivity}
        disabled={!activity.trim() || isSubmitting}
      >
        <LogButtonText>
          {isSubmitting ? t('logActivity.submitting') : t('logActivity.submit')}
        </LogButtonText>
      </LogButton>
      <FeedbackLink />
      <BottomNav />

      {/* Success Modal */}
      {showSuccessModal && (
        <SuccessModal
          onShareNow={handleShareNow}
          onMaybeLater={handleMaybeLater}
          userId={currentUser.uid}
          activityData={{
            title: activity,
            causeTitle: causes.find(c => c.id === selectedCause)?.title,
            causeId: selectedCause,
            levelTitle: causes.find(c => c.id === selectedCause)?.level?.title,
          }}
        />
      )}

      {/* Template Modal */}
      {showTemplateModal && (
        <div
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
          onClick={() => setShowTemplateModal(false)}
        >
          <div
            style={{ background: 'white', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: '400px', maxHeight: '60vh', display: 'flex', flexDirection: 'column' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #eee' }}>
              <span style={{ fontFamily: 'Poppins, sans-serif', fontSize: '16px', fontWeight: 600, color: theme.colors.deepNavy }}>{t('logActivity.myTemplates')}</span>
              <span style={{ fontSize: '20px', color: theme.colors.slateGray, cursor: 'pointer' }} onClick={() => setShowTemplateModal(false)}>✕</span>
            </div>
            <div style={{ overflowY: 'auto', padding: '8px 20px 20px' }}>
              {templates.map(template => (
                <div
                  key={template.id}
                  style={{ padding: '14px 0', borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}
                  onClick={() => handleApplyTemplate(template)}
                >
                  <div style={{ fontWeight: 600, fontSize: '14px', color: theme.colors.deepNavy, marginBottom: '2px' }}>{template.title}</div>
                  <div style={{ fontSize: '12px', color: theme.colors.slateGray }}>{template.causeTitle}</div>
                </div>
              ))}
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <span style={{ color: '#007bff', fontSize: '13px', cursor: 'pointer' }} onClick={() => { setShowTemplateModal(false); navigate('/my-templates'); }}>{t('logActivity.manageTemplates')}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && savedActivity && (
        <ShareModal
          activity={savedActivity}
          onClose={handleCloseShareModal}
        />
      )}

      {/* Milestone Modal */}
      {currentMilestone && (
        <MilestoneModal
          milestoneKey={currentMilestone}
          onClose={handleMilestoneDismiss}
        />
      )}
    </Container>
  );
};

export default LogActivityScreen;
